import { STATUS } from '@front-app/constants/state';
import { computed, Ref, ref } from '@vue/composition-api';

/**
 * @deprecated in favour of useAsyncStatusDeprecated2
 */
export const useAsyncStatusDeprecated = (p: Promise<unknown>): Ref<STATUS> => {
	const status = ref(STATUS.LOADING);
	p
		.then(() => {
			status.value = STATUS.SUCCESS;
		})
		.catch(() => {
			status.value = STATUS.ERROR;
		});
	return status;
};

export const useIsStatus = (status: Ref<STATUS>, testStatus: STATUS): Ref<boolean> => computed(() => status.value === testStatus);

export const useIsUnstarted = (status: Ref<STATUS>): Ref<boolean> => useIsStatus(status, STATUS.UNSTARTED);
export const useIsLoading = (status: Ref<STATUS>): Ref<boolean> => useIsStatus(status, STATUS.LOADING);
export const useIsSuccess = (status: Ref<STATUS>): Ref<boolean> => useIsStatus(status, STATUS.SUCCESS);
export const useIsError = (status: Ref<STATUS>): Ref<boolean> => useIsStatus(status, STATUS.ERROR);

export const useAreLoading = (statuses: Ref<STATUS[]>): Ref<boolean> => computed(() => statuses.value.some((s) => s === STATUS.LOADING));

export interface AsyncStatus {
	track: <T>(p: Promise<T>, config?: AsyncStatusConfig) => Promise<T>;
	clear: (timeoutDuration: number) => void;
	status: Ref<STATUS>;
	isError: Ref<boolean>;
	isLoading: Ref<boolean>;
	isSuccess: Ref<boolean>;
	isUnstarted: Ref<boolean>;
}

export interface AsyncStatusConfig {
	shouldHandleError: (error: unknown) => boolean;
}

const DEFAULT_CONFIG = {
	shouldHandleError: () => true,
};

/**
 * Allows tracking of asynchronous calls bound to a single STATUS reference, with convenience
 * boolean Refs for specific status values as well.
 *
 * Usage:
 *
 * ```
 * const { track, status, isError } = useAsyncStatus();
 * const apiResponse = track(someApiCall());
 *
 * // In template
 * <div v-if="isError">The api call has failed</div>
 * ```
 *
 * @param outerStatus Optional parameter to be bound to the status tracking. Useful if for
 * some reason you need to define the status variable outside of this helper
 * @returns AsyncStatus
 */
export const useAsyncStatus = (outerStatus?: Ref<STATUS>, config?: Partial<AsyncStatusConfig>): AsyncStatus => {
	// eslint-disable-next-line prefer-object-spread
	const innerConfig = Object.assign({}, DEFAULT_CONFIG, config);
	const status = outerStatus || ref(STATUS.UNSTARTED);
	let tId = -1;
	const clear = (timeoutDuration = 2000) => {
		tId = window.setTimeout(() => {
			status.value = STATUS.UNSTARTED;
		}, timeoutDuration);
	};
	const track = <T>(p: Promise<T>): Promise<T> => {
		window.clearTimeout(tId);
		status.value = STATUS.LOADING;
		return p
			.then((val: T) => {
				status.value = STATUS.SUCCESS;
				return val;
			})
			.catch((e) => {
				if (innerConfig.shouldHandleError(e)) {
					status.value = STATUS.ERROR;
				}
				return Promise.reject(e);
			});
	};
	return {
		track,
		clear,
		status,
		isError: useIsError(status),
		isLoading: useIsLoading(status),
		isSuccess: useIsSuccess(status),
		isUnstarted: useIsUnstarted(status),
	};
};

export interface UseStatus {
	isError: Ref<boolean>;
	isLoading: Ref<boolean>;
	isSuccess: Ref<boolean>;
	isUnstarted: Ref<boolean>;
}

export const useStatus = (status: Ref<STATUS>): UseStatus => ({
	isError: useIsError(status),
	isLoading: useIsLoading(status),
	isSuccess: useIsSuccess(status),
	isUnstarted: useIsUnstarted(status),
});
